import React from 'react';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Container, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectCalc } from './redux/calcSlice';
import UnitSelector from './components/UnitSelector';
import Calculator from './components/Calculator';
import Results from './components/Results';
import ContactUs from './components/ContactUs';

import './assets/css/App.css';

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 450,
      md: 600,
      lg: 900,
      xl: 1190
    }
  },
  typography: {
    fontFamily: 'TeeFranklinForeverMedium',
    p: {
      fontSize: '22px',
      fontFamily: 'TeeFranklinForeverBook',
      color: '#003057',
      lineHeight: '26px',
      marginBottom: '32px'
    },
    h1: {
      fontFamily: 'TeeFranklinForeverBook',
      fontSize: '55px',
      letterSpacing: '0px',
      color: '#003057',
      lineHeight: '55px',
      marginBottom: '24px'
    },
    h2: {
      fontFamily: 'TeeFranklinForeverBook',
      fontSize: '35px',
      letterSpacing: '0px',
      textDecoration: 'capitalize',
      color: '#009EE8',
      lineHeight: '55px',
      marginBottom: '24px'
    },
    h3: {
      fontFamily: 'TeeFranklinForeverMedium',
      fontSize: '16px',
      letterSpacing: '0px',
      marginBottom: '0px'
    },
    label: {
      fontFamily: 'TeeFranklinForeverLight',
      fontSize: '16px',
      color: '#003057',
      marginBottom: '8px',
      marginTop: '18px'
    }
  },
  palette: {
    type: 'light',
    primary: {
      main: '#009EE8',
      dark: '#003057',
      contrastText: '#fff'
    },
    secondary: {
      main: '#f50057',
    },
    text: {
      primary: '#003057',
      secondary: '#003057',
      disabled: '#A5A9AB',
    },
    background: {
      default: '#FFF',
    },
    paper: {
      default: '#F2F9FD',
      grey: '#F0F1F2'
    },
    form: {
      label:{
        padding: '2em'
      }
    }
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft:'0 !important',
          paddingRight:'0 !important'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: '#F2F9FD',
          boxShadow: 'none',
          borderRadius: 0
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          marginBottom: '0px',
          background: '#FFF',
          "&$disabled": {
            color: 'red',
            border: '2px solid red',
            background: 'red'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: 'TeeFranklinForeverLight',
          fontSize: '16px',
          height: '50px',
          borderRadius: '0px',
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'TeeFranklinForeverMedium',
          fontSize: '16px',
          marginTop:'42px',
          padding:'10px',
          boxShadow: 'none !important',
          textTransform: 'none',
          letterSpacing: '1.92px'
        }
      }
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius:0
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          background: '#009EE8',
          color: '#FFF',
          '&:hover': {
            backgroundColor: '#003057',
            color: '#FFF'
          },
          '&.Mui-selected': {
            backgroundColor: '#003057',
            color: '#FFF'
          },
          '&.Mui-selected:hover': {
            backgroundColor: '#003057',
            color: '#FFF'
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
         root: {
           border: '1px solid #003057',
         }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: '4px 0 0 0',
          color: '#d32f2f'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '35px',
          lineHeight: '35px',
          fontFamily: 'TeeFranklinForeverBook',
        }
      }
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          fontFamily: 'TeeFranklinForeverLight',
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root:{
          alignItems:'start',
          margin:0
        },
        label: {
          fontSize: '16px',
          lineHeight: '21px',
          fontFamily: 'TeeFranklinForeverLight'
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root:{
          alignItems:'baseline'
        }
      }
    }
  }
});

const App = () => {
  const calc = useSelector(selectCalc);
  
  return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="xl">
          <header>
            {/* <Typography variant="h1" component="h2">Storage tank calculator</Typography> */}
            <Typography variant="p" component="p">Storage tank shell thickness calculation tool based on the API 650 and EN 14015 standards allows you to estimate material consumption for the cylindrical shell of a tank and to compare how steel grade affects material consumption. This tool can be used for estimation only. We are happy to discuss your requirements in more detail.</Typography>
          </header>
          <UnitSelector />
          <Calculator />
          {calc.submitted &&
          <Results />}
          <ContactUs />
          <footer></footer>
        </Container>
      </ThemeProvider>
  );
}

export default App;
